"use client"

import type { Event } from "openapi/api/model"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { Trash2 } from "lucide-react"
import { EventInviteSection } from "./calendar-event-shared"
import {
  EventBasicsSection,
  EventLinkingSection,
  EventLocationNotesSection,
  EventScheduleSection,
} from "./calendar-event-form-sections"
import { useCalendarEventDialogController } from "@/hooks/use-calendar-event-dialog-controller"

type CalendarEventDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultValues?: Partial<CalendarEventFormDataV2>
  mode: "create" | "edit"
  selectedEvent?: Event | null
}

export function CalendarEventDialog({
  open,
  onOpenChange,
  defaultValues,
  mode,
  selectedEvent,
}: CalendarEventDialogProps) {
  const {
    register,
    handleSubmit,
    setValue,
    selectedDate,
    startTime,
    endTime,
    linkType,
    selectedJob,
    selectedCandidate,
    isPresetCandidate,
    isPresetJob,
    sendInvites,
    includeInviteMessage,
    inviteeEmails,
    newAttachments,
    existingAttachments,
    removeAttachmentIds,
    eventType,
    jobs,
    candidates,
    overlappingBusySlots,
    inviteeDraft,
    setInviteeDraft,
    handleInviteeDraftKeyDown,
    handleInviteeDraftBlur,
    removeInviteeEmail,
    handleStartTimeChange,
    handleEndTimeChange,
    datePickerOpen,
    setDatePickerOpen,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isSubmitting,
    isBusySlotsPending,
    handleFormSubmit,
    handleDelete,
    supportsInviteMessage,
    allowAttachments,
  } = useCalendarEventDialogController({
    open,
    onOpenChange,
    defaultValues,
    mode,
    selectedEvent,
  })

  const formDisabled = isSubmitting || isBusySlotsPending

  const handleDeleteClick = async () => {
    if (showDeleteConfirm) {
      await handleDelete()
      setShowDeleteConfirm(false)
      return
    }
    setShowDeleteConfirm(true)
  }

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={mode === "create" ? "Create Event" : "Edit Event"}
      footer={
        <>
          {mode === "edit" && selectedEvent?.id && (
            /* Asks twice, in place, rather than opening a dialog over this one. */
            <QuietAction
              size="md"
              tone="destructive"
              onClick={handleDeleteClick}
              disabled={formDisabled}
              className="@sm:mr-auto"
            >
              <Trash2 className="size-[14px]" />
              {showDeleteConfirm ? "Confirm delete" : "Delete Event"}
            </QuietAction>
          )}
          <QuietAction size="md" onClick={() => onOpenChange(false)} disabled={formDisabled}>
            Cancel
          </QuietAction>
          <PageAction tone="primary" type="submit" form="calendar-event-form" disabled={formDisabled}>
            {isSubmitting
              ? mode === "create"
                ? "Creating…"
                : "Saving…"
              : mode === "create"
                ? "Create Event"
                : "Save Changes"}
          </PageAction>
        </>
      }
    >
        <form id="calendar-event-form" onSubmit={handleSubmit(handleFormSubmit)}>
          <fieldset disabled={formDisabled} className="disabled:opacity-80">
            <EventBasicsSection eventType={eventType} setValue={setValue} register={register} />

            <EventLinkingSection
              linkType={linkType}
              selectedJob={selectedJob ?? ""}
              selectedCandidate={selectedCandidate ?? ""}
              jobs={jobs}
              candidates={candidates}
              setValue={setValue}
              isPresetCandidate={isPresetCandidate}
              isPresetJob={isPresetJob}
            />

            <EventScheduleSection
              datePickerOpen={datePickerOpen}
              setDatePickerOpen={setDatePickerOpen}
              selectedDate={selectedDate}
              startTime={startTime}
              endTime={endTime}
              onDateSelect={(date) => setValue("date", date)}
              onStartTimeChange={handleStartTimeChange}
              onEndTimeChange={handleEndTimeChange}
              overlappingBusySlots={overlappingBusySlots}
            />

            <EventLocationNotesSection register={register} />

            <EventInviteSection
              sendInvites={sendInvites}
              onSendInvitesChange={(checked) => setValue("send_invites", checked)}
              inviteeDraft={inviteeDraft}
              onInviteeDraftChange={setInviteeDraft}
              onInviteeDraftKeyDown={handleInviteeDraftKeyDown}
              onInviteeDraftBlur={handleInviteeDraftBlur}
              inviteeEmails={inviteeEmails}
              onRemoveInviteeEmail={removeInviteeEmail}
              includeInviteMessage={!!includeInviteMessage}
              onIncludeInviteMessageChange={(checked) => setValue("include_invite_message", checked)}
              inviteMessageField={register("invite_message")}
              existingAttachments={existingAttachments}
              newAttachments={newAttachments}
              onAddAttachments={(files) =>
                setValue("attachments", [...newAttachments, ...files], { shouldDirty: true })
              }
              onRemoveExistingAttachment={(id) => {
                setValue(
                  "existing_attachments",
                  existingAttachments.filter((attachment) => attachment.id !== id),
                  { shouldDirty: true },
                )
                setValue(
                  "remove_attachment_ids",
                  Array.from(new Set([...removeAttachmentIds, id])),
                  { shouldDirty: true },
                )
              }}
              onRemoveNewAttachment={(index) =>
                setValue(
                  "attachments",
                  newAttachments.filter((_, attachmentIndex) => attachmentIndex !== index),
                  { shouldDirty: true },
                )
              }
              showAttachments={allowAttachments}
              showCandidateEmailHint={linkType === "candidate"}
              attendeeEditMode={mode === "edit"}
              supportsInviteMessage={supportsInviteMessage}
            />
          </fieldset>

          {isBusySlotsPending && (
            <p className="mt-4 font-mono text-[11px] text-muted-foreground">
              Checking the calendar…
            </p>
          )}
        </form>
    </DialogShell>
  )
}
